import { resolveDisplayedStatus } from '@/lib/presence';
import type { GuildMemberRow, GuildRole, UserStatus } from '@/types/api';

/** dataStore.presence — online flag keyed by userId; absent = unknown. */
export type PresenceMap = Record<string, boolean | undefined>;

export interface RosterEntry {
  member: GuildMemberRow;
  status: UserStatus;
}

export interface RosterBucket {
  role: GuildRole | null;
  entries: RosterEntry[];
}

export interface Roster {
  /** Role buckets ordered by role position desc; the "no coloured role"
   *  bucket (role === null) always lands last. */
  buckets: RosterBucket[];
  /** Flat, de-duplicated, name-sorted list of offline members. */
  offline: RosterEntry[];
  totalOnline: number;
  totalOffline: number;
}

const FALLBACK_KEY = '__default__';

function byName(a: GuildMemberRow, b: GuildMemberRow): number {
  return (a.user.displayName || a.user.username).localeCompare(
    b.user.displayName || b.user.username,
    'ru',
  );
}

/**
 * Group guild members the Discord way: by their highest-positioned coloured
 * role, online above offline, name-sorted within a bucket. Members with no
 * coloured role fall into a trailing default bucket. Pure — no React — so it
 * can be unit-tested and shared by the Aether and HeroUI member panels.
 */
export function buildRoster(
  members: GuildMemberRow[],
  rolesById: Map<string, GuildRole>,
  presence: PresenceMap,
  myId: string | null,
): Roster {
  const byRoleId = new Map<string, RosterBucket>();
  for (const m of members) {
    const status = resolveDisplayedStatus(m.user.status, presence[m.userId], m.userId === myId);
    let topRole: GuildRole | null = null;
    for (const id of m.roleIds) {
      const role = rolesById.get(id);
      if (!role || role.color === 0) continue;
      if (!topRole || role.position > topRole.position) topRole = role;
    }
    const key = topRole?.id ?? FALLBACK_KEY;
    const bucket = byRoleId.get(key) ?? { role: topRole, entries: [] };
    bucket.entries.push({ member: m, status });
    byRoleId.set(key, bucket);
  }

  const buckets = [...byRoleId.values()].sort((a, b) => {
    if (a.role && !b.role) return -1;
    if (!a.role && b.role) return 1;
    if (a.role && b.role) return b.role.position - a.role.position;
    return 0;
  });

  for (const bucket of buckets) {
    bucket.entries.sort((a, b) => {
      const ao = a.status === 'OFFLINE' ? 1 : 0;
      const bo = b.status === 'OFFLINE' ? 1 : 0;
      if (ao !== bo) return ao - bo;
      return byName(a.member, b.member);
    });
  }

  const offlineSeen = new Set<string>();
  const offline: RosterEntry[] = [];
  for (const bucket of buckets) {
    for (const e of bucket.entries) {
      if (e.status !== 'OFFLINE') continue;
      if (offlineSeen.has(e.member.userId)) continue;
      offlineSeen.add(e.member.userId);
      offline.push(e);
    }
  }
  offline.sort((a, b) => byName(a.member, b.member));

  const totalOnline = buckets.reduce(
    (n, g) => n + g.entries.filter((e) => e.status !== 'OFFLINE').length,
    0,
  );

  return { buckets, offline, totalOnline, totalOffline: members.length - totalOnline };
}
